import { useEffect, useMemo, type Dispatch, type SetStateAction } from "react";
import {
  ManufacturingComboboxPicker,
  type ComboboxOption,
} from "@/components/manufacturing/ManufacturingComboboxPicker";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  applyRecipeToCompanyRateDraft,
  findRecipeForLaborMatchKey,
} from "@/lib/manufacturing/laborCompanyRateRecipe";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import type { CompanyRateDraft } from "./useManufacturingLaborCompanyRates";
import { ManufacturingLaborPaysBySelect } from "./ManufacturingLaborPaysBySelect";

export function ManufacturingLaborCompanyRateForm({
  draft,
  setDraft,
  editing,
  busy,
  recipes,
  placeholder = "Search product SKUs…",
  onSave,
  onCancel,
}: {
  draft: CompanyRateDraft;
  setDraft: Dispatch<SetStateAction<CompanyRateDraft>>;
  editing: boolean;
  busy: boolean;
  recipes: MfgRecipeSummary[];
  placeholder?: string;
  onSave: () => void;
  onCancel: () => void;
}) {
  const options = useMemo<ComboboxOption<MfgRecipeSummary>[]>(() => {
    return recipes
      .filter((r) => r.is_active || r.id === draft.recipe_id)
      .map((r) => ({
        key: r.id,
        label: `${r.code} — ${r.name}`,
        sublabel: r.kind,
        data: r,
      }));
  }, [draft.recipe_id, recipes]);

  useEffect(() => {
    if (draft.recipe_id || !draft.searchHint) return;
    const hit = findRecipeForLaborMatchKey(recipes, draft.searchHint);
    if (!hit) return;
    setDraft((prev) => applyRecipeToCompanyRateDraft(prev, hit));
  }, [draft.recipe_id, draft.searchHint, recipes, setDraft]);

  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-900/40">
      <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-zinc-100">
        {editing ? "Edit rate" : "Add rate"}
      </h4>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Match key
          <ManufacturingComboboxPicker<MfgRecipeSummary>
            value={draft.recipe_id}
            options={options}
            placeholder={placeholder}
            initialQuery={draft.searchHint}
            disabled={busy}
            onPick={(opt) => setDraft((prev) => applyRecipeToCompanyRateDraft(prev, opt.data))}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Label
          <input
            className={inputClass}
            value={draft.label}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          $ rate
          <input
            className={inputClass}
            type="number"
            min={0}
            step="0.01"
            value={draft.flat_rate_usd}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, flat_rate_usd: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Pays by
          <ManufacturingLaborPaysBySelect
            value={draft.pays_by}
            disabled={busy}
            onChange={(pays_by) => setDraft({ ...draft, pays_by })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Notes
          <input
            className={inputClass}
            value={draft.notes}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          />
        </label>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={busy || !draft.recipe_id || !draft.flat_rate_usd.trim() || !draft.pays_by}
          onClick={onSave}
        >
          Save
        </button>
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
